// cSpell:ignore Afrekening Décompte départ Eindafrekening huurcontract vertrek
// The name of a cost settlement, wherever it is shown: the dashboard's lists
// and detail, its statements and payment requests, and the history
// (ActivityText). A settlement is named by its kind and period (Lewis,
// 2026-10-16), never by a free name:
// - a regular settlement: "Afrekening 2026" for a calendar year,
//   "Afrekening 01/01–30/06/2026" within one year,
//   "Afrekening 01/07/2026–30/06/2027" across years;
// - a departure: "Eindafrekening bij vertrek" (short "Eindafrekening"),
//   towards the tenant "Eindafrekening bij vertrek — huurcontract tot
//   30/06/2026".
// EN "Settlement" / "Final settlement on departure", FR "Décompte" /
// "Décompte final au départ".
import { endOfYear, getYear, isAfter, isSameDay, normalizeDate, startOfYear, } from "./date-fns.utils";
import { formatDate, toDay } from "./date.utils";
import { getLocalizedText, toLocale } from "./i18n/i18n.utils";
var ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
// The day a period bound names, as a date. Both bounds are optional and the
// dashboard's mapper writes "" for a missing one, so an unusable bound gives
// undefined and the title falls back to the plain word.
var parseDay = function (iso) {
    var day = toDay(iso);
    return day && ISO_DAY.test(day) ? normalizeDate(day) : undefined;
};
// The period as a regular settlement's title prints it: the year alone for a
// calendar year, the year once for a period within one year, both dates in
// full otherwise (also for an end before the start, which no settlement
// should have: the dates then read as given rather than as a wrong year).
var regularTitle = function (params) {
    var locale = params.locale;
    var from = parseDay(params.from);
    var to = parseDay(params.to);
    if (!from || !to) {
        return getLocalizedText("settlement.title.regular", {}, 0, locale);
    }
    var isWithinOneYear = !isAfter(from, to) && getYear(from) === getYear(to);
    if (isWithinOneYear &&
        isSameDay(from, startOfYear(from)) &&
        isSameDay(to, endOfYear(to))) {
        return getLocalizedText("settlement.title.year", { year: formatDate(from, "yyyy") }, 0, locale);
    }
    return getLocalizedText("settlement.title.period", {
        from: formatDate(from, isWithinOneYear ? "dd/MM" : "dd/MM/yyyy"),
        to: formatDate(to),
    }, 0, locale);
};
// A settlement's title in `locale` (a dashboard locale or a contact's
// language; the reader's language when absent). `from` and `to` are its
// period, as yyyy-mm-dd; for a departure, `to` is the lease's last day.
// `short` gives a departure's short title, `withLeaseEnd` the one towards the
// tenant that names the lease's last day.
export var getSettlementTitle = function (params) {
    var _a = params.kind, kind = _a === void 0 ? "regular" : _a, from = params.from, to = params.to, short = params.short, withLeaseEnd = params.withLeaseEnd;
    var locale = toLocale(params.locale);
    if (kind !== "departure")
        return regularTitle({ from: from, to: to, locale: locale });
    if (short) {
        return getLocalizedText("settlement.title.departure_short", {}, 0, locale);
    }
    var until = parseDay(to);
    if (withLeaseEnd && until) {
        return getLocalizedText("settlement.title.departure_until", { until: formatDate(until) }, 0, locale);
    }
    return getLocalizedText("settlement.title.departure", {}, 0, locale);
};
